/**
 * Card renderer for the Storyblok Visual Editor preview.
 *
 * Mirrors the mobile-app ContentCard component design:
 *   - Light page background (#F8F7F4)
 *   - White card with rounded corners + soft drop shadow
 *   - Circular icon placeholder (brand orange)
 *   - Bold header text
 *   - Body text (markdown)
 *   - Pill-shaped "Learn more" CTA button
 *
 * Three entrypoints depending on which story the editor opened:
 *   - <CardPagePreview>     : story.component === 'content_page'    → grid of cards
 *   - <CardCategoryPreview> : story.component === 'content_category' → category of cards
 *   - <ContentCardPreview>  : story.component === 'content_card'    → single card
 */

import React, { useState } from 'react';
import { Box, Stack, Typography, Alert, Dialog, DialogContent, IconButton } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { storyblokEditable } from '@storyblok/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  extractHtmlDocument,
  isFullHtmlDocument,
  richTextToPlainText,
  resolvePreviewHtml,
  withPreviewDocumentStyles,
} from './htmlPreview';

// ── Theme tokens ────────────────────────────────────────────────────
const COLORS = {
  pageBg: '#F8F7F4',
  cardBg: '#FFFFFF',
  iconBg: '#F0F0F0',       // light grey — actual brand color set per tenant
  iconColor: '#0D1C3D',
  headerText: '#0D1C3D',
  bodyText: '#4A546A',
  btnBorder: '#0D1C3D',
  btnText: '#0D1C3D',
};

const CARD_SHADOW = '0px 4px 20px rgba(0, 0, 0, 0.08)';
const CARD_RADIUS = '16px';

// ── Types ────────────────────────────────────────────────────────────

interface StoryblokRel {
  _uid?: string;
  _editable?: string;
}

interface StoryblokAsset {
  filename?: string;
  alt?: string;
}

interface ContentCardBlok extends StoryblokRel {
  component: 'content_card';
  title?: string | { type: string; content?: unknown[] }; // plain string or Storyblok richtext
  description?: string | { type: string; content?: unknown[] }; // string or Storyblok richtext
  icon?: StoryblokAsset;
  /** image is a multi-asset field in Storyblok — comes back as an array */
  image?: StoryblokAsset | StoryblokAsset[];
  cta_text?: string | { type: string; content?: unknown[] }; // plain string or Storyblok richtext
}

/** Extracts filename from either a single asset object or a multi-asset array */
function assetFilename(field: StoryblokAsset | StoryblokAsset[] | undefined): string | undefined {
  if (!field) return undefined;
  const asset = Array.isArray(field) ? field[0] : field;
  return asset?.filename || undefined;
}

interface ResolvedItem {
  uuid?: string;
  slug?: string;
  content?: ContentCardBlok;
}

interface CategoryBlok extends StoryblokRel {
  component: 'content_category';
  // `name` is the content_category schema's display field (what create/rename
  // write and editors change); `category_name` is a legacy out-of-schema key.
  name?: string;
  category_name?: string;
  items?: ResolvedItem[] | string[];
}

interface ResolvedCategory {
  uuid?: string;
  name?: string;
  content?: CategoryBlok;
}

interface ContentPageBlok extends StoryblokRel {
  component: 'content_page';
  categories?: ResolvedCategory[] | string[];
  /** Flat content types (cards) store items directly on the page, not in categories. */
  items?: ResolvedItem[] | string[];
}

// ── Helpers ─────────────────────────────────────────────────────────

function isResolved<T extends object>(x: unknown): x is T {
  return typeof x === 'object' && x !== null;
}

/** Deduplicate cohort variant stories by base slug (strips --everyone / --coh-<hash>). */
function dedupeItems(itemsField: Array<ResolvedItem | string>): ResolvedItem[] {
  const seenUuids = new Set<string>();
  const seenSlugs = new Set<string>();
  const out: ResolvedItem[] = [];
  for (const it of itemsField) {
    if (!isResolved<ResolvedItem>(it)) continue;
    if (it.uuid && seenUuids.has(it.uuid)) continue;
    if (it.uuid) seenUuids.add(it.uuid);
    const base = it.slug?.replace(/--(?:everyone|coh-[a-f0-9]+)$/i, '');
    if (base && seenSlugs.has(base)) continue;
    if (base) seenSlugs.add(base);
    out.push(it);
  }
  return out;
}

function editableProps(blok: StoryblokRel | undefined): Record<string, string> {
  if (!blok) return {};
  return storyblokEditable(blok as unknown as Parameters<typeof storyblokEditable>[0]) as Record<string, string>;
}

interface MdProps { children?: React.ReactNode; href?: string; }

/** Extracts plain text from a Storyblok richtext node or returns string as-is. */
export function resolveDescription(desc: unknown): string | null {
  return richTextToPlainText(desc as any);
}

function MarkdownBody({ text }: { text: string | undefined | null }) {
  if (!text) return null;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }: MdProps) => (
          <Box component="p" sx={{ m: 0, '& + p': { mt: 1 }, fontSize: 14, color: COLORS.bodyText, lineHeight: 1.6 }}>
            {children}
          </Box>
        ),
        a: ({ href, children }: MdProps) => (
          <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.headerText }}>
            {children}
          </a>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

// ── Card detail modal ────────────────────────────────────────────────

function CardDetailModal({ blok, open, onClose }: { blok: ContentCardBlok; open: boolean; onClose: () => void }) {
  const imageUrl = assetFilename(blok.image);
  const descriptionHtml = extractHtmlDocument(blok.description);
  const descriptionText = resolveDescription(blok.description);
  const hasHtmlDocument = !!descriptionHtml && isFullHtmlDocument(descriptionHtml);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header image */}
      {imageUrl && (
        <Box sx={{ position: 'relative', width: '100%', height: 200, overflow: 'hidden', flexShrink: 0 }}>
          <img
            src={imageUrl}
            alt={(Array.isArray(blok.image) ? blok.image[0]?.alt : blok.image?.alt) || blok.title || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute', top: 8, right: 8,
              bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {!imageUrl && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <DialogContent sx={{ pt: 3 }}>
        {!hasHtmlDocument && resolvePreviewHtml(blok.title as any) && (
          <Typography
            sx={{ fontWeight: 700, fontSize: 20, color: COLORS.headerText, mb: 1.5, lineHeight: 1.3 }}
            dangerouslySetInnerHTML={{ __html: resolvePreviewHtml(blok.title as any) }}
          />
        )}
        {descriptionHtml ? (
          <Box sx={{ width: '100%', height: 560, overflow: 'hidden' }}>
            <iframe
              srcDoc={withPreviewDocumentStyles(descriptionHtml)}
              title={blok.title || 'Content card body'}
              sandbox="allow-scripts"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          </Box>
        ) : descriptionText && (
          <Box>
            <MarkdownBody text={descriptionText} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Single content_card tile ─────────────────────────────────────────

interface CardTileProps {
  blok: ContentCardBlok;
}

function CardTile({ blok }: CardTileProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const ctaHtml = resolvePreviewHtml(blok.cta_text as any) || 'Learn more';
  const titleHtml = resolvePreviewHtml(blok.title as any);
  const iconUrl = assetFilename(blok.icon);

  return (
    <>
      <Box
        {...editableProps(blok)}
        sx={{
          background: COLORS.cardBg,
          borderRadius: CARD_RADIUS,
          boxShadow: CARD_SHADOW,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: 280,
        }}
      >
        {/* Icon circle */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: COLORS.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {iconUrl ? (
            <img
              src={iconUrl}
              alt={blok.icon?.alt || ''}
              style={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          ) : (
            <ArticleOutlinedIcon sx={{ color: COLORS.iconColor, fontSize: 22 }} />
          )}
        </Box>

        {/* Title */}
        {titleHtml ? (
          <Typography
            sx={{ fontWeight: 700, fontSize: 15, color: COLORS.headerText, lineHeight: 1.35 }}
            dangerouslySetInnerHTML={{ __html: titleHtml }}
          />
        ) : (
          <Typography sx={{ fontSize: 14, color: COLORS.bodyText, fontStyle: 'italic' }}>
            (no title set)
          </Typography>
        )}

        {/* CTA pill button */}
        <Box
          component="button"
          onClick={() => setModalOpen(true)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            alignSelf: 'flex-start',
            mt: 'auto',
            px: 2.5,
            py: 1,
            border: `1.5px solid ${COLORS.btnBorder}`,
            borderRadius: '99px',
            background: 'transparent',
            color: COLORS.btnText,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: ctaHtml }} />
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      <CardDetailModal blok={blok} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

// ── Page-level preview ───────────────────────────────────────────────

export interface CardPagePreviewProps {
  pageBlok: ContentPageBlok;
}

export function CardPagePreview({ pageBlok }: CardPagePreviewProps) {
  // Cards are flat (no categories) — items live directly on the page story.
  const itemsField = (pageBlok.items ?? []) as Array<ResolvedItem | string>;
  const allCards: ContentCardBlok[] = dedupeItems(itemsField)
    .map((it) => it.content)
    .filter((c): c is ContentCardBlok => !!c);

  const unresolvedCount = itemsField.filter((it) => typeof it === 'string').length;

  return (
    <Box {...editableProps(pageBlok)} sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      {unresolvedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {unresolvedCount} item{unresolvedCount > 1 ? 's' : ''} not resolved — ensure{' '}
          <code>resolve_relations</code> includes <code>content_page.items</code>.
        </Alert>
      )}

      {allCards.length === 0 ? (
        <Alert severity="info">
          No cards yet. Add a <code>content_card</code> story inside this page's folder.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {allCards.map((card, i) => (
            <CardTile key={card._uid || i} blok={card} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Category-level preview ───────────────────────────────────────────

export interface CardCategoryPreviewProps {
  categoryBlok: CategoryBlok;
}

export function CardCategoryPreview({ categoryBlok }: CardCategoryPreviewProps) {
  const items = (categoryBlok.items ?? []) as Array<ResolvedItem | string>;
  const cards: ContentCardBlok[] = items
    .filter((it): it is ResolvedItem => isResolved<ResolvedItem>(it))
    .map((it) => it.content)
    .filter((c): c is ContentCardBlok => !!c);

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      {(categoryBlok.name || categoryBlok.category_name) && (
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: COLORS.headerText, mb: 3 }}>
          {categoryBlok.name || categoryBlok.category_name}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {cards.map((card, i) => (
          <CardTile key={card._uid || i} blok={card} />
        ))}
      </Box>
    </Box>
  );
}

// ── Single card preview ──────────────────────────────────────────────

export interface ContentCardPreviewProps {
  cardBlok: ContentCardBlok;
}

export function ContentCardPreview({ cardBlok }: ContentCardPreviewProps) {
  return (
    <Box
      sx={{
        bgcolor: COLORS.pageBg,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack spacing={2} alignItems="flex-start">
        <CardTile blok={cardBlok} />
      </Stack>
    </Box>
  );
}
